import { useState, useEffect, useRef, useCallback } from 'react';

export const AUTO_REFRESH_MS = 30000;

/** @param {Date | null} date */
export function formatRefreshAgo(date) {
  if (!date) return '';
  const s = Math.floor((Date.now() - date.getTime()) / 1000);
  if (s < 5) return 'just now';
  if (s < 60) return `${s}s ago`;
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  return `${Math.floor(s / 3600)}h ago`;
}

/**
 * Polls `fetchFn` on mount and every `intervalMs`. After the first load, refreshes
 * in the background without toggling `loading` (uses `refreshing` instead).
 */
export function useAutoRefresh(fetchFn, deps = [], intervalMs = AUTO_REFRESH_MS) {
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(null);
  const hasLoadedRef = useRef(false);
  const mountedRef = useRef(true);

  const refresh = useCallback(
    async ({ silent = false } = {}) => {
      const background = silent || hasLoadedRef.current;
      if (!background) setLoading(true);
      else setRefreshing(true);
      try {
        await fetchFn();
        if (mountedRef.current) {
          setLastUpdated(new Date());
          hasLoadedRef.current = true;
        }
      } finally {
        if (mountedRef.current) {
          setLoading(false);
          setRefreshing(false);
        }
      }
    },
    [fetchFn]
  );

  useEffect(() => {
    hasLoadedRef.current = false;
  }, deps);

  useEffect(() => {
    mountedRef.current = true;
    refresh();
    const id = setInterval(() => refresh({ silent: true }), intervalMs);
    return () => {
      mountedRef.current = false;
      clearInterval(id);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...deps, intervalMs]);

  return {
    loading,
    refreshing,
    lastUpdated,
    refresh: () => refresh({ silent: false }),
    hasLoaded: hasLoadedRef.current
  };
}

export function AutoRefreshBadge({ refreshing, lastUpdated, className = '' }) {
  if (!lastUpdated && !refreshing) return null;
  return (
    <span
      className={`inline-flex items-center gap-1.5 text-xs text-gray-500 dark:text-gray-400 ${className}`}
      title={lastUpdated ? lastUpdated.toLocaleString() : undefined}
    >
      {refreshing && (
        <span className="inline-block w-2 h-2 rounded-full bg-blue-500 dark:bg-blue-400 animate-pulse shrink-0" />
      )}
      {refreshing ? 'Updating…' : `Auto-refresh · ${formatRefreshAgo(lastUpdated)}`}
    </span>
  );
}
